import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { useAudio } from '../contexts/AudioContext';
import '../styles/Results.css';

const Results = () => {
  const navigate = useNavigate();
  const { gameState, honeyTypes, resetGame } = useGame();
  const { stopMusic, playMusic, playSound } = useAudio();
  const [score, setScore] = useState(0);

  // Verificar si el juego ha finalizado
  useEffect(() => {
    // Asegurarse de que el puntaje sea un número válido
    const validScore = typeof gameState.score === 'number' && !isNaN(gameState.score) 
      ? gameState.score 
      : 0;
    
    setScore(validScore);
    
    // Redireccionar si el juego no ha terminado
    if (!gameState.finished) {
      navigate('/');
    }
  }, [gameState, navigate]);

  // Calcular el número de aciertos
  const correctCount = gameState.selections.reduce((count, selection, index) => {
    return selection === gameState.correctAnswers[index] ? count + 1 : count;
  }, 0);

  // Reproducir sonido según el resultado
  useEffect(() => {
    if (gameState.finished) {
      if (correctCount === 3) {
        playSound('correct_answer');
      } else if (correctCount === 0) {
        playSound('wrong_answer');
      } else {
        playSound('game_start');
      }
    }
  }, [correctCount, gameState.finished, playSound]);

  // Obtener mensaje según el desempeño
  const getPerformanceMessage = () => {
    switch (correctCount) {
      case 3:
        return '¡Maestro de la Miel!';
      case 2:
        return '¡Casi lo lográs!';
      case 1:
        return '¡Sigue practicando!';
      default:
        return '¡Inténtalo de nuevo!';
    }
  };

  // Obtener animación según el desempeño
  const getPerformanceAnimation = () => {
    switch (correctCount) {
      case 3:
        return 'crown-animation';
      case 2:
        return 'clapping-animation';
      default:
        return 'magnifier-animation';
    }
  };

  // Manejar el reinicio del juego
  const handlePlayAgain = () => {
    resetGame();
    // Detener cualquier música que esté sonando antes de navegar
    stopMusic();
    navigate('/game');
  };

  // Manejar el regreso al inicio
  const handleBackToHome = () => {
    resetGame();
    // Detener cualquier música que esté sonando antes de navegar
    stopMusic();
    // Navegar a la página principal
    navigate('/');
    // Reproducir la música de la página principal después de un breve retraso
    // para asegurar que la navegación se haya completado
    setTimeout(() => {
      playMusic();
    }, 100);
  };

  return (
    <div className="results-container">
      <div className="results-content">
        <div className={`performance-animation ${getPerformanceAnimation()}`}></div>
        
        <h1 className="results-title">Resultados</h1>
        
        <div className="performance-message">
          <h2>{getPerformanceMessage()}</h2>
          <p>Acertaste {correctCount} de 3 mieles</p>
        </div>
        
        <div className="score-display">
          <div className="score-circle">
            <span className="score-value">{score}</span>
            <span className="score-label">puntos</span>
          </div>
        </div>
        
        <div className="results-details">
          <h3>Detalle de tus respuestas</h3>
          
          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>Muestra</th>
                  <th>Tu selección</th>
                  <th>Miel correcta</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {gameState.selections.map((selection, index) => {
                  const isCorrect = selection === gameState.correctAnswers[index];
                  const selectedHoney = honeyTypes.find(h => h.id === selection);
                  const correctHoney = honeyTypes.find(h => h.id === gameState.correctAnswers[index]);
                  
                  return (
                    <tr key={index} className={isCorrect ? 'correct-row' : 'incorrect-row'}>
                      <td>Muestra {index + 1}</td>
                      <td>{selectedHoney ? selectedHoney.name : '-'}</td>
                      <td>{correctHoney ? correctHoney.name : '-'}</td>
                      <td>
                        <span className={`result-icon ${isCorrect ? 'correct' : 'incorrect'}`}>
                          {isCorrect ? '✓' : '✗'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-label">Pistas utilizadas:</span>
              <span className="stat-value">{gameState.hintsUsed}</span>
            </div>
            {gameState.timeElapsed > 0 && (
              <div className="stat-item">
                <span className="stat-label">Tiempo empleado:</span>
                <span className="stat-value">{formatTime(gameState.timeElapsed)}</span>
              </div>
            )}
          </div>
        </div>
        
        {gameState.competitiveMode && (
          <div className="leaderboard-preview">
            <h3>Tabla de Clasificación</h3>
            <div className="leaderboard-table-container">
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th>Posición</th>
                    <th>Jugador</th>
                    <th>Puntuación</th>
                    <th>Aciertos</th>
                  </tr>
                </thead>
                <tbody>
                  {gameState.leaderboard.slice(0, 5).map((entry, index) => (
                    <tr 
                      key={index} 
                      className={entry.name === gameState.playerName ? 'current-player' : ''}
                    >
                      <td>{index + 1}</td>
                      <td>{entry.name}</td>
                      <td>{entry.score}</td>
                      <td>{entry.correctAnswers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        <div className="results-actions">
          <button 
            className="btn btn-primary"
            onClick={handlePlayAgain}
          >
            Jugar de nuevo
          </button>
          <button 
            className="btn btn-secondary"
            onClick={handleBackToHome}
          >
            Volver al inicio
          </button>
        </div>
        
        <div className="honey-facts">
          <h3>¿Sabías que...?</h3>
          <p>{getRandomHoneyFact()}</p>
        </div>
      </div>
    </div>
  );
};

// Función auxiliar para formatear el tiempo
function formatTime(seconds) {
  if (!seconds) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Función para obtener un dato curioso aleatorio sobre la miel
function getRandomHoneyFact() {
  const facts = [
    "La miel es el único alimento producido por insectos que los humanos consumen regularmente.",
    "Las abejas deben visitar aproximadamente 2 millones de flores para producir medio kilo de miel.",
    "La miel no caduca. Se han encontrado tarros de miel en tumbas egipcias de más de 3,000 años que aún son comestibles.",
    "Una abeja obrera produce aproximadamente 1/12 de una cucharadita de miel en toda su vida.",
    "El color y sabor de la miel varía según las flores de las que las abejas recolectan el néctar.",
    "La miel tiene propiedades antibacterianas y antisépticas naturales.",
    "Las abejas comunican la ubicación de las fuentes de néctar a otras abejas mediante una 'danza' especial.",
    "Una colmena puede contener hasta 60,000 abejas en su punto máximo."
  ];
  
  return facts[Math.floor(Math.random() * facts.length)];
}

export default Results;
